import React, { useState, useContext } from 'react';
import { AuthContext } from './AuthProvider';
import { useNavigate } from 'react-router-dom';
import { getCSRFToken, fetchAndGetCSRFToken } from './utils/csrf';
import { defaultRedirectForRole } from './routeAccess';

function Login() {
  const { login } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    console.log('[Login] Submitting login form');
    try {
      await fetchAndGetCSRFToken();
      const csrfToken = getCSRFToken();
      // console.log('[Login] Calling login() with:', { email, password, code });
      const result = await login(email, password, code, csrfToken);
      // console.log('[Login] login() result:', result);

      if (result.success && result.requiresMfaSetup) {
        setLoading(false);
        navigate('/mfa-setup', { state: { email, mfaSetupPending: true } });
        return;
      }

      if (result.success) {
        setLoading(false);
        const redirectPath = defaultRedirectForRole(result.role);
        navigate(redirectPath);
        setLoading(false);
        return;
      }

      if (result.requiresPassword) {
        setShowPasswordField(true);
        setError('Please enter your password to continue.');
        setLoading(false);
        return;
      }

      if (result.requiresMfaVerify) {
        setShowCode(true);
        setError('Enter your authenticator code.');
        setLoading(false);
        return;
      }
      
      setError(result.message || 'Invalid credentials.');
    } catch (err) {
      console.error('[Login] Login error:', err);
      setLoading(false);
      setError('Login failed.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center">Login</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3"
            />
          </div>
          {showPasswordField && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <div className="relative mt-1">
                <input
                  type={passwordVisible ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  className="block w-full rounded-md border border-gray-300 py-2 px-3 pr-10"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                >
                  {passwordVisible ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          )}
          {showCode && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Authenticator code</label>
              <input
                type="text"
                value={code}
                onChange={e => setCode(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3"
              />
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-6 bg-indigo-600 text-white rounded-md shadow hover:bg-indigo-700 transition-all"
          >
            {(!showPasswordField && !showCode) ? (loading ? 'Verifying Email' : 'Verify Email') : (loading ? 'Logging in...' : 'Login')}

            {/* {loading ? 'Logging in...' : 'Login'} */}
          </button>
          {error && <div className="mt-4 text-red-600 text-center">{error}</div>}
        </form>
      </div>
    </div>
  );
}

export default Login;
